/**
 * /internal/integrations — first-class internal page for integration analytics
 * and suggested-integration curation (VERA-405).
 *
 * Existing tab: analytics over installed/active integrations.
 * Suggested tab: demand chart + curation queue (graduated from Labs).
 * Roadmap tab: our hand-ranked master list of candidate integrations (RoadmapTab.tsx).
 *
 * TODO(VERA-405): gate behind an internal 'integrations.view' permission once added to the catalog
 */
import React, { useMemo, useState } from 'react'
import {
  ArrowUpIcon,
  ArrowUpRightIcon,
  CheckIcon,
  ChevronDownIcon,
  FlaskConicalIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

import { Button, Card, Input, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { Skeleton } from '@repo/ui'
import { Header, IntegrationLogo, SelectField } from 'components'
import { useColorMode } from 'hooks'
import {
  useIntegrationAnalytics,
  useIntegrationVoters,
  useInternalIntegrationRequests,
  usePromoteIntegrationRequest,
  useDeclineIntegrationRequest,
} from 'hooks/api'
import type { IntegrationAnalytics, InternalIntegrationRequest } from 'services/integrationRequestsApi'
import {
  INTEGRATION_CATEGORY_LABELS,
  type IntegrationCategory,
} from 'constants/integrations'
import { date } from 'utils'
import { useSnackbar } from 'contexts'
import { RoadmapTab } from './RoadmapTab'

// ─── Types ─────────────────────────────────────────────────────────────────

type GranularityOption = 'day' | 'week' | 'month'
type TabValue = 'existing' | 'suggested' | 'roadmap'
type SortOption = 'votes' | 'recent'

// ─── Constants ────────────────────────────────────────────────────────────

const TABS = [
  { label: 'Existing', value: 'existing' as TabValue },
  { label: 'Votes', value: 'suggested' as TabValue },
  { label: 'Roadmap', value: 'roadmap' as TabValue },
]

const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

// Date range driving the analytics `days` param (so charts can reach further back).
const RANGE_OPTIONS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last year' },
  { value: '730', label: 'All time' },
]

const SORT_OPTIONS = [
  { value: 'votes', label: 'Most votes' },
  { value: 'recent', label: 'Recent' },
]

const SUMMARY_SORT_OPTIONS = [
  { value: 'installs', label: 'Installs' },
  { value: 'active', label: 'Active' },
  { value: 'votes', label: 'Votes' },
]

const CATEGORY_OPTIONS = (Object.keys(INTEGRATION_CATEGORY_LABELS) as IntegrationCategory[]).map(
  (c) => ({ value: c, label: INTEGRATION_CATEGORY_LABELS[c] }),
)

// Palette of distinct colors for multi-series lines/bars
const CHART_COLORS = [
  '#6366f1', // indigo
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
]

// Dark-aware recharts tooltip props (the default tooltip box is white — unreadable
// on the dark chart). theme.palette via useTheme resolves the LIGHT scheme under
// the CSS-vars theme, so we key off the resolved color mode and use literal colors.
const tooltipProps = (isDark: boolean) => ({
  contentStyle: {
    fontSize: 12,
    borderRadius: 8,
    background: isDark ? '#1f1f23' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#e4e4e7'}`,
    color: isDark ? '#fafafa' : '#18181b',
  },
  itemStyle: { color: isDark ? '#fafafa' : '#18181b' },
  labelStyle: { color: isDark ? '#a1a1aa' : '#71717a', marginBottom: 2 },
  cursor: { fill: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
})

// ─── Chart skeletons ──────────────────────────────────────────────────────

const ChartSkeleton = ({ height = 220 }: { height?: number }) => (
  <Box sx={{ width: '100%', height, borderRadius: 2, overflow: 'hidden' }}>
    <Skeleton height={height} />
  </Box>
)

// ─── Empty state ──────────────────────────────────────────────────────────

const ChartEmpty = ({ message }: { message: string }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 220,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  )
}

// ─── Voter accordion (per suggestion card) ────────────────────────────────

interface VoterAccordionProps {
  requestId: string
  voteCount: number
  submitterEmail?: string | null
  submitterReason?: string | null
}

const VoterAccordion = ({ requestId, voteCount, submitterEmail, submitterReason }: VoterAccordionProps) => {
  const [expanded, setExpanded] = useState(false)
  const [search, setSearch] = useState('')
  const { data: voters = [], isLoading } = useIntegrationVoters(requestId, expanded)

  const filteredVoters = useMemo(() => {
    if (!search.trim()) return voters
    const q = search.toLowerCase()
    return voters.filter(
      (v) =>
        v.email.toLowerCase().includes(q) ||
        (v.company ?? '').toLowerCase().includes(q) ||
        (v.email === submitterEmail && (submitterReason ?? '').toLowerCase().includes(q)),
    )
  }, [voters, search, submitterEmail, submitterReason])

  return (
    <Accordion
      expanded={expanded}
      onChange={(_: React.SyntheticEvent, v: boolean) => setExpanded(v)}
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        // Transparent so it reads as part of the card. MUI dark mode paints a
        // lighter "elevation overlay" via background-image on Paper — clear both.
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        '&.Mui-expanded': { backgroundColor: 'transparent', backgroundImage: 'none', margin: 0 },
        '& .MuiAccordionSummary-root, & .MuiAccordionDetails-root': {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDownIcon size={16} strokeWidth={1.5} />}
        sx={{
          px: 0,
          minHeight: 'unset',
          // Pin the expanded state so the "N voters" row doesn't grow/shift when
          // the accordion opens (MUI bumps summary min-height + content margin on
          // expand by default, which made the header jump + flash).
          '&.Mui-expanded': { minHeight: 'unset' },
          '& .MuiAccordionSummary-content': { my: 0.5, '&.Mui-expanded': { my: 0.5 } },
        }}
      >
        <Typography variant="body3" color="text.secondary">
          {voteCount} voter{voteCount !== 1 ? 's' : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pb: 0 }}>
        {/* Search input */}
        <Box sx={{ mb: 1.5 }}>
          <Input
            placeholder="Search voters..."
            value={search}
            onValueChange={setSearch}
            startAdornment={<SearchIcon size={16} strokeWidth={1} />}
          />
        </Box>

        {isLoading ? (
          <Skeleton height={80} />
        ) : voters.length === 0 ? (
          <Typography variant="body3" color="text.secondary">
            No voters recorded.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
            <MuiTable size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Email</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Company</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Voted</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVoters.map((v) => (
                  <TableRow key={v.email}>
                    <TableCell sx={{ fontSize: 12 }}>{v.email}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.company ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {date(v.votedAt)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {v.reason || (v.email === submitterEmail && submitterReason ? submitterReason : '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

// ─── Existing tab — install/active analytics ─────────────────────────────

interface ExistingTabProps {
  analytics: IntegrationAnalytics | undefined
  isLoading: boolean
  selectedCategories: string[]
  selectedIntegrations: string[]
}

const ExistingTab = ({
  analytics,
  isLoading,
  selectedCategories,
  selectedIntegrations,
}: ExistingTabProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  const [summarySort, setSummarySort] = useState<'installs' | 'votes' | 'active'>('installs')

  const filteredSummary = useMemo(() => {
    if (!analytics) return []
    return analytics.summary.filter((s) => {
      const catMatch =
        selectedCategories.length === 0 ||
        s.categories.some((c) => selectedCategories.includes(c))
      const intMatch =
        selectedIntegrations.length === 0 || selectedIntegrations.includes(s.slug)
      return catMatch && intMatch
    })
  }, [analytics, selectedCategories, selectedIntegrations])

  const sortedSummary = useMemo(() => {
    const key = summarySort === 'active' ? 'activeInstalls' : summarySort
    return [...filteredSummary].sort((a, b) => (b[key] as number) - (a[key] as number))
  }, [filteredSummary, summarySort])

  const lineData = useMemo(() => {
    if (!analytics) return []
    return analytics.periods.map((period, i) => {
      const point: Record<string, string | number> = {
        period,
        Total: analytics.installs.total[i] ?? 0,
      }
      filteredSummary.forEach((s) => {
        point[s.name] = analytics.installs.bySlug[s.slug]?.[i] ?? 0
      })
      return point
    })
  }, [analytics, filteredSummary])

  const hasLineData =
    lineData.length > 0 &&
    lineData.some(
      (d) =>
        (d.Total as number) > 0 ||
        filteredSummary.some((s) => (d[s.name] as number) > 0),
    )

  const barData = useMemo(
    () =>
      filteredSummary.map((s) => ({
        name: s.name,
        Installs: s.installs,
        Votes: s.votes,
      })),
    [filteredSummary],
  )

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <ChartSkeleton height={240} />
        <ChartSkeleton height={240} />
        <Stack spacing={2}>
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} height={56} />
          ))}
        </Stack>
      </Stack>
    )
  }

  if (!analytics) return null

  return (
    <Stack spacing={3}>
      {/* Installs over time line chart */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          Installs over time
        </Typography>
        {!hasLineData ? (
          <ChartEmpty message="No install data in this period." />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lineData} margin={{ top: 4, right: 16, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                allowDecimals={false}
              />
              <Tooltip {...tooltipProps(isDark)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="Total"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
                strokeDasharray="4 2"
              />
              {filteredSummary.map((s, idx) => (
                <Line
                  key={s.slug}
                  type="monotone"
                  dataKey={s.name}
                  stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                  strokeWidth={1.5}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Install/votes ranking bar chart — dual Y-axis so installs and votes
          are each on their own scale (installs e.g. 64 would dwarf votes e.g. 12
          on a shared axis). */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          Ranking
        </Typography>
        {barData.length === 0 ? (
          <ChartEmpty message="No integrations match the current filters." />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} margin={{ top: 4, right: 40, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                allowDecimals={false}
              />
              <Tooltip {...tooltipProps(isDark)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                yAxisId="left"
                dataKey="Installs"
                fill={theme.palette.brand?.[600] || '#7F48DF'}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="right"
                dataKey="Votes"
                fill={isDark ? '#a1a1aa' : '#71717a'}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Summary list */}
      <Card sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="subtitle1">Summary</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.secondary">Sort by</Typography>
            <Box sx={{ minWidth: 130 }}>
              <SelectField
                value={summarySort}
                options={SUMMARY_SORT_OPTIONS}
                onChange={(v) => setSummarySort(v as 'installs' | 'votes' | 'active')}
              />
            </Box>
          </Stack>
        </Stack>
        {sortedSummary.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No integrations match the current filters.
          </Typography>
        ) : (
          <Stack
            spacing={2}
            divider={
              <Box
                sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}
              />
            }
          >
            {sortedSummary.map((s) => (
              <Stack key={s.slug} direction="row" alignItems="center" spacing={2} sx={{ py: 0.5 }}>
                <IntegrationLogo
                  src={s.logo}
                  alt={s.name}
                  size={36}
                  fallback={
                    <Typography variant="body3" fontWeight={600}>
                      {s.name.charAt(0).toUpperCase()}
                    </Typography>
                  }
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {s.name}
                  </Typography>
                  {s.categories.length > 0 && (
                    <Typography variant="body4" color="text.secondary" sx={{ mt: 0.25 }}>
                      {s.categories
                        .map((c) => INTEGRATION_CATEGORY_LABELS[c as IntegrationCategory] ?? c)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={0.25} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                  <Typography variant="body3" fontWeight={500}>
                    {s.installs.toLocaleString()} installs
                  </Typography>
                  <Typography variant="body4" color="text.secondary">
                    {s.activeInstalls.toLocaleString()} active now · {s.votes.toLocaleString()} votes
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>
    </Stack>
  )
}

// ─── Suggested curation list with voter accordion ─────────────────────────

interface SuggestedCurationListProps {
  suggestions: InternalIntegrationRequest[]
  isLoading: boolean
  analytics: IntegrationAnalytics | undefined
}

const SuggestedCurationList = ({
  suggestions,
  isLoading,
  analytics,
}: SuggestedCurationListProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  const { open: notification } = useSnackbar()
  const promote = usePromoteIntegrationRequest()
  const decline = useDeclineIntegrationRequest()

  const [edited, setEdited] = useState<Record<string, string[]>>({})
  const [sort, setSort] = useState<SortOption>('votes')

  const categoriesFor = (id: string, suggested: string[]) => edited[id] ?? suggested ?? []
  const setCategories = (id: string, cats: string[]) =>
    setEdited((p) => ({ ...p, [id]: cats }))

  const handlePromote = (id: string, name: string, cats: string[]) =>
    promote.mutate(
      { id, categories: cats },
      {
        // No success toast: the row leaves the pending-review queue on invalidation.
        onError: (e: any) =>
          notification({ type: 'error', title: e?.message || 'Failed to promote.' }),
      },
    )

  const handleDecline = (id: string, name: string) =>
    decline.mutate(id, {
      // No success toast: the row leaves the pending-review queue on invalidation.
      onError: (e: any) =>
        notification({ type: 'error', title: e?.message || 'Failed to decline.' }),
    })

  const sortedSuggestions = useMemo(() => {
    const copy = [...suggestions]
    if (sort === 'votes') {
      copy.sort((a, b) => {
        if (b.voteCount !== a.voteCount) return b.voteCount - a.voteCount
        return a.name.localeCompare(b.name)
      })
    } else {
      copy.sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return db - da
      })
    }
    return copy
  }, [suggestions, sort])

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((n) => (
          <Card key={n} sx={{ p: 4 }}>
            <Skeleton height={96} />
          </Card>
        ))}
      </Stack>
    )
  }

  if (suggestions.length === 0) {
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 6,
          minHeight: 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center">
          <FlaskConicalIcon size={32} strokeWidth={1.25} color={theme.palette.text.secondary} />
          <Typography variant="body2" color="text.secondary">
            No suggestions yet.
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Stack spacing={1}>
          <Typography variant="h6">Integrations Voting</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}. Assign categories, then
            promote to publish to the vote list, or decline to hide.
          </Typography>
        </Stack>
        <Box sx={{ minWidth: 160 }}>
          <SelectField
            label="Sort by"
            value={sort}
            options={SORT_OPTIONS}
            onChange={(v) => setSort(v as SortOption)}
          />
        </Box>
      </Stack>

      {sortedSuggestions.map((row) => {
        const picked = categoriesFor(row.id, row.categories)
        const host = row.url ? row.url.replace(/^https?:\/\//, '') : null
        const demandSeries = analytics?.demand.bySlug[row.slug]

        // Build demand data for the persistent mini chart (no useMemo — hooks cannot
        // be called inside a .map() callback; compute inline instead)
        const demandData =
          demandSeries && analytics?.periods && demandSeries.length > 0
            ? analytics.periods.map((p, i) => ({ period: p, votes: demandSeries[i] ?? 0 }))
            : []
        const hasDemandData = demandData.length > 0 && demandData.some((d) => d.votes > 0)

        return (
          <Card key={row.id} sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Stack direction="row" alignItems="flex-start" spacing={3}>
                <IntegrationLogo
                  src={row.logo}
                  alt={row.name}
                  fallback={
                    <Typography variant="h6" color="text.secondary">
                      {row.name.charAt(0).toUpperCase()}
                    </Typography>
                  }
                />
                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {row.name}
                  </Typography>
                  {host && (
                    <Link
                      href={row.url!}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      color="text.secondary"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        width: 'fit-content',
                        fontSize: 13,
                      }}
                    >
                      {host}
                      <ArrowUpRightIcon size={13} strokeWidth={1.5} />
                    </Link>
                  )}
                  {row.description && (
                    <Typography
                      variant="body3"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5, pt: 0.5 }}
                    >
                      {row.description}
                    </Typography>
                  )}
                </Stack>

                {/* Vote count + category assignment, grouped top-right */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 0.5, flexShrink: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: theme.palette.text.secondary }}>
                    <ArrowUpIcon size={14} strokeWidth={2} />
                    <Typography variant="body3" sx={{ fontWeight: 500 }}>
                      {row.voteCount}
                    </Typography>
                  </Stack>
                  <Box sx={{ width: 200 }}>
                    <SelectField
                      multiple
                      search
                      value={picked}
                      options={CATEGORY_OPTIONS}
                      placeholder="Categories"
                      onChange={(v) => setCategories(row.id, v as string[])}
                    />
                  </Box>
                </Stack>
              </Stack>

              {/* Persistent Votes mini chart */}
              {hasDemandData && (
                <Box>
                  <Typography variant="body3" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Votes
                  </Typography>
                  <ResponsiveContainer width="100%" height={90}>
                    <LineChart data={demandData} margin={{ top: 4, right: 8, left: -32, bottom: 0 }}>
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip {...tooltipProps(isDark)} />
                      <Line
                        type="monotone"
                        dataKey="votes"
                        stroke={theme.palette.primary.main}
                        strokeWidth={1.5}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Voters accordion — expands to show only the voters table */}
              <VoterAccordion
                requestId={row.id}
                voteCount={row.voteCount}
                submitterEmail={row.submitterEmail}
                submitterReason={row.submitterReason}
              />

              {/* Curation actions only while a suggestion is under review
                  (Promote/Decline require status='pending_review'). */}
              {row.status === 'pending_review' && (
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                  <Button
                    variant="secondary"
                    destructive="hover"
                    disableElevation
                    startIcon={<XIcon size={14} strokeWidth={2} />}
                    disabled={decline.isPending}
                    onClick={() => handleDecline(row.id, row.name)}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="primary"
                    disableElevation
                    startIcon={<CheckIcon size={14} strokeWidth={2} />}
                    disabled={promote.isPending || picked.length === 0}
                    onClick={() => handlePromote(row.id, row.name, picked)}
                  >
                    Promote
                  </Button>
                </Stack>
              )}
            </Stack>
          </Card>
        )
      })}
    </Stack>
  )
}

// ─── Suggested tab — demand analytics + curation queue ────────────────────

interface SuggestedTabProps {
  analytics: IntegrationAnalytics | undefined
  isLoading: boolean
  selectedCategories: string[]
  selectedIntegrations: string[]
}

const SuggestedTab = ({
  analytics,
  isLoading,
  selectedCategories,
  selectedIntegrations,
}: SuggestedTabProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'
  const { data: suggestions = [], isLoading: isSuggestionsLoading } =
    useInternalIntegrationRequests('all')

  const filteredSlugs = useMemo(() => {
    if (!analytics) return []
    return Object.keys(analytics.demand.bySlug).filter((slug) => {
      if (selectedIntegrations.length > 0 && !selectedIntegrations.includes(slug)) return false
      if (selectedCategories.length > 0) {
        const match = suggestions.find((s) => s.slug === slug)
        if (match && !match.categories.some((c) => selectedCategories.includes(c))) return false
      }
      return true
    })
  }, [analytics, selectedIntegrations, selectedCategories, suggestions])

  const demandLineData = useMemo(() => {
    if (!analytics) return []
    return analytics.periods.map((period, i) => {
      const point: Record<string, string | number> = {
        period,
        Total: analytics.demand.total[i] ?? 0,
      }
      filteredSlugs.forEach((slug) => {
        const label = suggestions.find((s) => s.slug === slug)?.name ?? slug
        point[label] = analytics.demand.bySlug[slug]?.[i] ?? 0
      })
      return point
    })
  }, [analytics, filteredSlugs, suggestions])

  const hasDemandData = demandLineData.some(
    (d) =>
      (d.Total as number) > 0 ||
      filteredSlugs.some((slug) => {
        const label = suggestions.find((s) => s.slug === slug)?.name ?? slug
        return (d[label] as number) > 0
      }),
  )

  return (
    <Stack spacing={3}>
      {/* Demand over time */}
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          Demand (votes) over time
        </Typography>
        {isLoading ? (
          <ChartSkeleton height={220} />
        ) : !analytics || !hasDemandData ? (
          <ChartEmpty message="No demand data in this period." />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={demandLineData}
              margin={{ top: 4, right: 16, left: -24, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                allowDecimals={false}
              />
              <Tooltip {...tooltipProps(isDark)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="Total"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
                strokeDasharray="4 2"
              />
              {filteredSlugs.map((slug, idx) => {
                const label = suggestions.find((s) => s.slug === slug)?.name ?? slug
                return (
                  <Line
                    key={slug}
                    type="monotone"
                    dataKey={label}
                    stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                    strokeWidth={1.5}
                    dot={false}
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Curation queue */}
      <SuggestedCurationList
        suggestions={suggestions}
        isLoading={isSuggestionsLoading}
        analytics={analytics}
      />
    </Stack>
  )
}

// ─── Shared filter bar ─────────────────────────────────────────────────────

interface FilterBarProps {
  granularity: GranularityOption
  onGranularityChange: (v: GranularityOption) => void
  days: number
  onDaysChange: (v: number) => void
  selectedCategories: string[]
  onCategoriesChange: (v: string[]) => void
  selectedIntegrations: string[]
  onIntegrationsChange: (v: string[]) => void
  integrationOptions: Array<{ value: string; label: string }>
}

const FilterBar = ({
  granularity,
  onGranularityChange,
  days,
  onDaysChange,
  selectedCategories,
  onCategoriesChange,
  selectedIntegrations,
  onIntegrationsChange,
  integrationOptions,
}: FilterBarProps) => (
  <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
    <Box sx={{ minWidth: 160 }}>
      <SelectField
        label="Range"
        value={String(days)}
        options={RANGE_OPTIONS}
        onChange={(v) => onDaysChange(Number(v))}
      />
    </Box>
    <Box sx={{ minWidth: 140 }}>
      <SelectField
        label="Granularity"
        value={granularity}
        options={GRANULARITY_OPTIONS}
        onChange={(v) => onGranularityChange(v as GranularityOption)}
      />
    </Box>
    <Box sx={{ minWidth: 180 }}>
      <SelectField
        label="Categories"
        multiple
        search
        value={selectedCategories}
        options={CATEGORY_OPTIONS}
        placeholder="All categories"
        onChange={(v) => onCategoriesChange(v as string[])}
      />
    </Box>
    {integrationOptions.length > 0 && (
      <Box sx={{ minWidth: 200 }}>
        <SelectField
          label="Integrations"
          multiple
          search
          value={selectedIntegrations}
          options={integrationOptions}
          placeholder="All integrations"
          onChange={(v) => onIntegrationsChange(v as string[])}
        />
      </Box>
    )}
  </Stack>
)

// ─── Main page ─────────────────────────────────────────────────────────────

export const InternalIntegrationsPage = () => {
  const [activeTab, setActiveTab] = useState<TabValue>('existing')
  const [granularity, setGranularity] = useState<GranularityOption>('week')
  const [days, setDays] = useState(180)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>([])

  const { data: analytics, isLoading: isAnalyticsLoading } = useIntegrationAnalytics({
    granularity,
    days,
  })

  const integrationOptions = useMemo(() => {
    if (!analytics) return []
    const seen = new Set<string>()
    const opts: Array<{ value: string; label: string }> = []
    analytics.summary.forEach((s) => {
      if (!seen.has(s.slug)) {
        seen.add(s.slug)
        opts.push({ value: s.slug, label: s.name })
      }
    })
    Object.keys(analytics.demand.bySlug).forEach((slug) => {
      if (!seen.has(slug)) {
        seen.add(slug)
        opts.push({ value: slug, label: slug })
      }
    })
    return opts
  }, [analytics])

  return (
    <>
      {/* VRG-470: underline page-header tabs. `redirect={false}` + a controlled value —
          these values are state keys, not routes. Gap below from TABS_CONTENT_GAP. */}
      <Header title="Integrations" />
      <Tabs
        redirect={false}
        tabs={TABS}
        value={activeTab}
        onChange={(v) => setActiveTab(v as TabValue)}
        sx={{ mb: `${TABS_CONTENT_GAP}px` }}
      />

      {activeTab === 'roadmap' ? (
        // The analytics filter bar (range, granularity, …) means nothing here; the
        // shared Table brings its own search, filter drawer and sort.
        <Box sx={{ px: 6 }}>
          <RoadmapTab />
        </Box>
      ) : (
      <Stack spacing={3} sx={{ p: 6, pt: 4 }}>
        <FilterBar
          granularity={granularity}
          onGranularityChange={setGranularity}
          days={days}
          onDaysChange={setDays}
          selectedCategories={selectedCategories}
          onCategoriesChange={setSelectedCategories}
          selectedIntegrations={selectedIntegrations}
          onIntegrationsChange={setSelectedIntegrations}
          integrationOptions={integrationOptions}
        />

        {activeTab === 'existing' ? (
          <ExistingTab
            analytics={analytics}
            isLoading={isAnalyticsLoading}
            selectedCategories={selectedCategories}
            selectedIntegrations={selectedIntegrations}
          />
        ) : (
          <SuggestedTab
            analytics={analytics}
            isLoading={isAnalyticsLoading}
            selectedCategories={selectedCategories}
            selectedIntegrations={selectedIntegrations}
          />
        )}
      </Stack>
      )}
    </>
  )
}
